import styled from 'styled-components';

import { getTableCss } from '@/comp/pages/EnginePage/Engine/Common/styled';
import { GlobalToken } from 'antd';

interface SensorsPriorityContainerProps {
  $hasFinishedWizard: boolean;
  $isReadOnly?: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const SensorsPriorityContainer = styled.div<SensorsPriorityContainerProps>`
  display: flex;
  flex-direction: column;
  padding-left: ${({ theme: { token } }) => token.paddingXL}px;
  max-height: ${({ $hasFinishedWizard }) => ($hasFinishedWizard ? '509px' : '426px')};
  overflow: scroll;
  ${({ $isReadOnly }) => $isReadOnly && 'pointer-events:none;'}

  .add-fallback-button {
    align-self: flex-end;
  }

  .sensors-multiselect {
    width: 100%;
  }

  .sensor-priority {
    display: flex;
    flex-direction: column;
    margin-bottom: ${({ theme: { token } }) => token.marginXL}px;
    color: ${({ theme: { token } }) => token.colorText};

    .select {
      width: 26%;
      margin-right: ${({ theme: { token } }) => token.marginLG}px;
    }

    .sensor-priority-header {
      display: flex;
      justify-content: space-between;
      background-color: ${({ theme: { token } }) => token.colorBgElevated};
      border-bottom: 2px solid ${({ theme: { token } }) => token.colorBorder};
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      padding: ${({ theme: { token } }) =>
        `${token.padding}px ${token.padding}px ${token.paddingSM}px ${token.paddingXS}px`};

      .priority-icons {
        display: flex;
        flex-shrink: 1;
        align-items: center;
      }

      .margin-right {
        margin-right: ${({ theme: { token } }) => token.marginSM}px;
      }

      .arrows {
        display: flex;
        align-items: center;
        margin-left: ${({ theme: { token } }) => token.marginXS}px;

        .arrow {
          width: 14px;
          cursor: pointer;
        }

        .transform-down {
          transform: rotate(180deg);
          margin-left: ${({ theme: { token } }) => token.marginXXS}px;
        }
      }
    }

    .header {
      display: flex;
      border-bottom: 1.5px solid ${({ theme: { token } }) => token.colorBorder};
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      padding: ${({ theme: { token } }) => `${token.padding}px 0 ${token.padding}px ${token.paddingXS}px`};
      font-size: ${({ theme: { token } }) => token.fontSize}px;
    }

    .logic {
      width: 20%;
    }

    .online {
      width: 20%;
    }

    .values {
      display: flex;
      align-items: center;
      border-bottom: 1px solid ${({ theme: { token } }) => token.colorBorder};
      padding: ${({ theme: { token } }) => `${token.paddingSM}px 0 ${token.paddingSM}px ${token.paddingXS}px`};
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};

      .sensors {
        display: flex;
        max-width: 65%;

        .sensor-name {
          border-radius: 6px;
          background-color: ${({ theme: { token } }) => token.colorBgElevated};
          padding: ${({ theme: { token } }) => token.paddingXXS}px;
          margin-right: ${({ theme: { token } }) => token.marginXXS}px;
          text-align: center;
        }
      }
    }
  }

  .default-behaviour {
    border: 0.5px solid ${({ theme: { token } }) => token.colorBorder};
    border-radius: 2px;
    padding: ${({ theme: { token } }) =>
      `${token.paddingXS}px ${token.paddingSM}px ${token.paddingSM}px ${token.paddingSM}px`};
    margin-top: ${({ theme: { token } }) => token.margin}px;

    .default-behaviour-label {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      margin-bottom: ${({ theme: { token } }) => token.margin}px;
    }

    .default-behaviour-radio-group {
      display: flex;
      height: 27px;
      align-items: center;

      .ant-select {
        width: 110px;
        margin-right: ${({ theme: { token } }) => token.marginXL}px;

        > .ant-select-selector {
          height: 27px;
          padding-left: ${({ theme: { token } }) => token.paddingSM}px;
          padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
          padding-bottom: ${({ theme: { token } }) => token.paddingXXS}px;
          padding-right: ${({ theme: { token } }) => token.padding}px;
          background-color: ${({ theme: { token } }) => token.colorBgElevated};
          border: 0.5px solid ${({ theme: { token } }) => token.colorBorder};

          .ant-select-selection-item {
            font-size: ${({ theme: { token } }) => token.fontSize}px;
          }
        }
      }

      > .ant-radio-wrapper {
        display: inline-flex;
        align-items: center;

        > span:last-child {
          padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
        }
      }

      .rule-generator-button {
        margin-right: ${({ theme: { token } }) => token.marginXL}px;
      }
    }
  }
`;

interface ResultContainerProps {
  $hasFinishedWizard: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const ResultContainer = styled.div<ResultContainerProps>`
  display: flex;
  flex-direction: column;
  padding-left: ${({ theme: { token } }) => token.paddingXL}px;
  max-height: ${({ $hasFinishedWizard }) => ($hasFinishedWizard ? '509px' : '426px')};
  overflow: scroll;

  ${({ theme: { token } }) => getTableCss(token)}

  .sensors-priority-table {
    color: ${({ theme: { token } }) => token.colorTextTertiary};
    margin-bottom: ${({ theme: { token } }) => token.marginXXL}px;

    .ant-table {
      background-color: ${({ theme: { token } }) => token.colorBgContainer};
      .ant-table-thead > tr > th {
        background-color: ${({ theme: { token } }) => token.colorBgElevated};
      }

      td.ant-table-cell:first-child {
        background-color: ${({ theme: { token } }) => token.colorBgElevated};
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }

      .ant-table-tbody > tr > td.ant-table-cell-row-hover:first-child {
        background-color: ${({ theme: { token } }) => token.colorBgElevated};
      }
    }

    .table-cell {
      color: ${({ theme: { token } }) => token.colorTextTertiary};
      display: flex;
      flex-wrap: wrap;
    }
  }

  .chip-item {
    border-radius: 6px;
    background-color: ${({ theme: { token } }) => token.colorBgElevated};
    padding: ${({ theme: { token } }) => token.paddingXXS}px;
    text-align: center;
    margin: ${({ theme: { token } }) => token.marginXXS}px ${({ theme: { token } }) => token.marginXXS}px
      ${({ theme: { token } }) => token.marginXXS}px 0;
    white-space: nowrap;
  }

  .header {
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    color: ${({ theme: { token } }) => token.colorText};
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    margin-bottom: ${({ theme: { token } }) => token.marginXS}px;
  }
`;

export const SelectContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-end;
  width: 465px;

  .compressors-multiselect {
    width: 100%;
  }
`;
